import React, { useState, useEffect, useRef } from 'react';

import { Autocomplete, AutocompleteProps, AutocompleteItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteClassCoachRoles from 'hooks/classCoachRole/useAutoCompleteClassCoachRoles';

import SelectorButton from './SelectorButton';

export interface ClassCoachRoleAutoCompleteValue extends AutocompleteItem {
  kiloClassCoachRoleId: string;
  label: string;
}

interface IClassCoachRoleAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: ClassCoachRoleAutoCompleteValue;
  onChange: (item: ClassCoachRoleAutoCompleteValue | null) => void;
}

const ClassCoachRoleAutoComplete = ({
  value,
  onChange,
  ...autocompleteProps
}: IClassCoachRoleAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<AutocompleteItem[]>([]);
  const [search, setSearch] = useState(value.label);
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isSuccess, isLoading } = useAutoCompleteClassCoachRoles({
    search: debouncedSearch,
  });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: ClassCoachRoleAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value.label || search)}
      handleClick={() => handleSearchChange('')}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.data.map(kccr => ({
          ...kccr,
          value: kccr.kiloClassCoachRoleId,
          label: kccr.name,
        })),
      );
    }
  }, [isSuccess, data]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      data={options}
      placeholder='Select coach role'
      {...autocompleteProps}
    />
  );
};

export default ClassCoachRoleAutoComplete;
